/**
 * FRVR local + cloud storage — in-memory Storage API with SDK backing.
 * Reads: cloud, then FRVR local, then browser localStorage.
 * Writes: every game key goes to both FRVR local and cloud. Deletes are flushed
 * in the same queue as writes so a cleared checkpoint cannot be restored.
 */

import { setGameStorageOverride, clearGameStorageOverride } from "./persistence-backend.js";
import {
  ALL_STORAGE_KEYS,
  StorageSlot,
  writeStorageRecord,
  readStorageRecord,
  PLAYER_ID_PATTERN,
} from "./storage.js";

/** @type {object | null} */
var boundSdk = null;
/** @type {Record<string, string>} */
var slotMap = {};
var flushTimer = null;
var flushDelayMs = 200;
var flushInFlight = null;
/** @type {Set<string>} */
var pendingWrites = new Set();
/** @type {Set<string>} */
var pendingDeletes = new Set();
/** @type {((event: Event) => void) | null} */
var pageHideListener = null;
/** @type {((event: Event) => void) | null} */
var visibilityListener = null;

function getBrowserStorage() {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

function scheduleFlush(sdk) {
  if (!sdk) return;
  if (flushTimer != null) clearTimeout(flushTimer);
  flushTimer = setTimeout(function () {
    flushTimer = null;
    flushFrvrGameStorageNow(sdk).catch(function (e) {
      console.warn("[frvr-persistence] flush failed:", e);
    });
  }, flushDelayMs);
}

async function applyToBoth(sdk, writeItems, deleteKeys) {
  var tasks = [];
  if (writeItems.length) {
    if (sdk.localStorage && typeof sdk.localStorage.setItems === "function") {
      tasks.push(sdk.localStorage.setItems(writeItems));
    }
    if (sdk.cloudStorage && typeof sdk.cloudStorage.setItems === "function") {
      tasks.push(sdk.cloudStorage.setItems(writeItems));
    }
  }
  if (deleteKeys.length) {
    if (sdk.localStorage && typeof sdk.localStorage.removeItems === "function") {
      tasks.push(sdk.localStorage.removeItems(deleteKeys));
    }
    if (sdk.cloudStorage && typeof sdk.cloudStorage.removeItems === "function") {
      tasks.push(sdk.cloudStorage.removeItems(deleteKeys));
    }
  }
  if (tasks.length) await Promise.all(tasks);
}

/**
 * @param {object} [sdk]
 * @returns {Promise<void>}
 */
export function flushFrvrGameStorageNow(sdk) {
  var target = sdk || boundSdk;
  if (flushInFlight) {
    return flushInFlight.then(function () {
      return flushFrvrGameStorageNow(target);
    });
  }
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!pendingWrites.size && !pendingDeletes.size) return Promise.resolve();
  if (!target) return Promise.resolve();

  var writeItems = [];
  pendingWrites.forEach(function (key) {
    if (Object.prototype.hasOwnProperty.call(slotMap, key)) {
      writeItems.push({ key: key, value: slotMap[key] });
    }
  });
  var deleteKeys = [];
  pendingDeletes.forEach(function (key) {
    if (!Object.prototype.hasOwnProperty.call(slotMap, key)) deleteKeys.push(key);
  });
  pendingWrites.clear();
  pendingDeletes.clear();
  if (!writeItems.length && !deleteKeys.length) return Promise.resolve();

  var failed = false;
  flushInFlight = applyToBoth(target, writeItems, deleteKeys).catch(function (err) {
    failed = true;
    for (var i = 0; i < writeItems.length; i++) {
      var item = writeItems[i];
      if (slotMap[item.key] === item.value) pendingWrites.add(item.key);
    }
    for (var j = 0; j < deleteKeys.length; j++) {
      if (!Object.prototype.hasOwnProperty.call(slotMap, deleteKeys[j])) {
        pendingDeletes.add(deleteKeys[j]);
      }
    }
    console.warn("[frvr-persistence] flush failed:", err);
  }).finally(function () {
    flushInFlight = null;
  });
  return flushInFlight.then(function () {
    if (failed) {
      scheduleFlush(target);
      return;
    }
    if (pendingWrites.size || pendingDeletes.size) return flushFrvrGameStorageNow(target);
  });
}

function installHideFlush() {
  if (typeof window === "undefined") return;
  removeHideFlush();
  pageHideListener = function () {
    flushFrvrGameStorageNow();
  };
  visibilityListener = function () {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      flushFrvrGameStorageNow();
    }
  };
  // Survives a test module reset so the next clear can detach the previous listeners.
  window.__frvrPersistenceHideFlush = {
    pageHide: pageHideListener,
    visibility: visibilityListener,
  };
  window.addEventListener("pagehide", pageHideListener);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", visibilityListener);
  }
}

function removeHideFlush() {
  var saved = typeof window !== "undefined" ? window.__frvrPersistenceHideFlush : null;
  var pageHide = pageHideListener || (saved && saved.pageHide);
  var visibility = visibilityListener || (saved && saved.visibility);
  if (typeof window !== "undefined" && pageHide) {
    window.removeEventListener("pagehide", pageHide);
  }
  if (typeof document !== "undefined" && visibility) {
    document.removeEventListener("visibilitychange", visibility);
  }
  if (typeof window !== "undefined") window.__frvrPersistenceHideFlush = null;
  pageHideListener = null;
  visibilityListener = null;
}

async function sdkGetItems(storageApi, keys) {
  if (!storageApi || typeof storageApi.getItems !== "function") return {};
  try {
    var items = await storageApi.getItems(keys);
    return items && typeof items === "object" ? items : {};
  } catch (e) {
    console.warn("[frvr-persistence] getItems failed:", e);
    return {};
  }
}

/** @param {object} sdk */
export async function hydrateFrvrGameStorage(sdk) {
  boundSdk = sdk || null;
  slotMap = {};
  pendingWrites.clear();
  pendingDeletes.clear();
  var browser = getBrowserStorage();
  var localItems = await sdkGetItems(sdk && sdk.localStorage, ALL_STORAGE_KEYS);
  var cloudItems = await sdkGetItems(sdk && sdk.cloudStorage, ALL_STORAGE_KEYS);

  for (var i = 0; i < ALL_STORAGE_KEYS.length; i++) {
    var key = ALL_STORAGE_KEYS[i];
    var fromSdk = cloudItems[key] != null ? cloudItems[key] : localItems[key];
    var value = fromSdk;
    if (value == null && browser) {
      try {
        value = browser.getItem(key);
      } catch (e) {
        value = null;
      }
      if (value != null) pendingWrites.add(key);
    }
    if (value != null) slotMap[key] = String(value);
  }
  if (pendingWrites.size) scheduleFlush(sdk);
}

/** @param {object} sdk @returns {Storage} */
export function createFrvrGameStorage(sdk) {
  boundSdk = sdk || boundSdk;
  installHideFlush();

  var storage = {
    get length() {
      return Object.keys(slotMap).length;
    },
    clear: function () {
      var keys = Object.keys(slotMap);
      for (var i = 0; i < ALL_STORAGE_KEYS.length; i++) {
        if (keys.indexOf(ALL_STORAGE_KEYS[i]) === -1) keys.push(ALL_STORAGE_KEYS[i]);
      }
      slotMap = {};
      pendingWrites.clear();
      for (var j = 0; j < keys.length; j++) pendingDeletes.add(keys[j]);
      scheduleFlush(sdk);
    },
    getItem: function (key) {
      return Object.prototype.hasOwnProperty.call(slotMap, key) ? slotMap[key] : null;
    },
    setItem: function (key, value) {
      slotMap[key] = String(value);
      pendingDeletes.delete(key);
      pendingWrites.add(key);
      scheduleFlush(sdk);
    },
    removeItem: function (key) {
      delete slotMap[key];
      pendingWrites.delete(key);
      pendingDeletes.add(key);
      scheduleFlush(sdk);
      var browser = getBrowserStorage();
      if (browser) {
        try {
          browser.removeItem(key);
        } catch (e) { /* ignore */ }
      }
    },
    key: function (index) {
      var keys = Object.keys(slotMap);
      return keys[index] != null ? keys[index] : null;
    },
  };

  setGameStorageOverride(/** @type {Storage} */ (storage));
  return /** @type {Storage} */ (storage);
}

export function clearFrvrGameStorage() {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  removeHideFlush();
  pendingWrites.clear();
  pendingDeletes.clear();
  slotMap = {};
  boundSdk = null;
  flushInFlight = null;
  clearGameStorageOverride();
}

/**
 * Prefer FRVR profile id for leaderboard when it is a UUID; otherwise keep/generate local UUID.
 * @param {() => string | null} getPlatformPlayerId
 * @param {Storage | null | undefined} storage
 * @returns {string | null}
 */
export function seedFrvrLeaderboardPlayerId(getPlatformPlayerId, storage) {
  var resolved = storage || getBrowserStorage();
  if (!resolved) return null;

  var existing = readStorageRecord(resolved, StorageSlot.PLAYER_ID);
  if (typeof existing === "string" && existing) {
    return existing;
  }

  var platformId = typeof getPlatformPlayerId === "function" ? getPlatformPlayerId() : null;
  if (platformId && PLAYER_ID_PATTERN.test(platformId)) {
    writeStorageRecord(resolved, StorageSlot.PLAYER_ID, platformId);
    return platformId;
  }

  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    var generated = crypto.randomUUID();
    writeStorageRecord(resolved, StorageSlot.PLAYER_ID, generated);
    return generated;
  }
  return null;
}
